import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import React from 'react';
import PropTypes from 'prop-types';
import compileRules from '@sprinx/pricing-core/compileRules';
import presetRule from '@sprinx/pricing-core/strategies/presetRule';
import presetTrigger from '@sprinx/pricing-core/strategies/presetTrigger';
import presetConstraint from '@sprinx/pricing-core/strategies/presetConstraint';
import presetMap from '@sprinx/pricing-core/strategies/presetMap';
import presetValueAdded from '@sprinx/pricing-core/strategies/presetValueAdded';
import presetInnerCondition from '@sprinx/pricing-core/strategies/presetInnerCondition';
import presetForceThreadFootprint from '@sprinx/pricing-core/strategies/presetForceThreadFootprint';
import useCancellablePromise from '@sprinx/react-hooks/useCancellablePromise';
import usePricingRulesServiceCallList from '../usePricingRulesServiceCallList';
import PricingRulesContext from './PricingRulesContext';

var usePricingRulesServiceCallListStatic = function usePricingRulesServiceCallListStatic() {
  return new Promise(function (resolve) {
    return resolve(null);
  });
};

var modifyArrayItem = function modifyArrayItem(arr, idx, cb) {
  return function (a) {
    a[idx] = cb(a[idx]);
    return a;
  }(_toConsumableArray(arr));
};

function PricingRulesProvider(_ref) {
  var children = _ref.children,
      currency = _ref.currency,
      decimalPrecision = _ref.decimalPrecision,
      initialRules = _ref.initialRules,
      initialRulesCompiled = _ref.initialRulesCompiled,
      noTaxPrices = _ref.noTaxPrices,
      priceGroup = _ref.priceGroup,
      productFinderHook = _ref.productFinderHook,
      region = _ref.region,
      rulesValidTreshold = _ref.rulesValidTreshold,
      staticBuild = _ref.staticBuild,
      pStrategies = _ref.strategies;
  var calcellablePromise = useCancellablePromise();

  var _React$useState = React.useState(initialRules || null),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      rules = _React$useState2[0],
      setRules = _React$useState2[1];

  var _React$useState3 = React.useState(initialRulesCompiled || null),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      compiledRules = _React$useState4[0],
      setCompiledRules = _React$useState4[1];

  var usePricingRulesServiceCallListFinal = staticBuild ? usePricingRulesServiceCallListStatic : usePricingRulesServiceCallList;
  var getAllRulesApiCall = usePricingRulesServiceCallListFinal();
  var strategies = React.useMemo(function () {
    return {
      rule: presetRule(pStrategies.rule),
      trigger: presetTrigger(pStrategies.trigger),
      constraint: presetConstraint(pStrategies.constraint),
      map: presetMap(pStrategies.map),
      valueAdded: presetValueAdded(pStrategies.valueAdded),
      innerCondition: presetInnerCondition(pStrategies.innerCondition),
      forceThreadFootprint: presetForceThreadFootprint(pStrategies.forceThreadFootprint)
    };
  }, [pStrategies]);
  React.useEffect(function () {
    if (rules === null && !staticBuild) getAllRulesApiCall().then(setRules);
  }, [getAllRulesApiCall, rules, staticBuild, initialRulesCompiled]);
  React.useEffect(function () {
    if (rules !== null) {
      calcellablePromise(compileRules({
        validTreshold: rulesValidTreshold
      }, rules)).then(setCompiledRules);
    }
  }, [calcellablePromise, rules, rulesValidTreshold, initialRulesCompiled]);
  var dynamicRuleDisable = React.useCallback(function (ruleCode) {
    setRules(function (rls) {
      var ridx = rls.findIndex(function (r) {
        return r.ruleCode === ruleCode;
      });
      return ridx === -1 ? rls : modifyArrayItem(rls, ridx, function (r) {
        return _extends({}, r, {
          disabled: true
        });
      });
    });
  }, []);
  var dynamicRuleEnable = React.useCallback(function (ruleCode, modifier) {
    return setRules(function (rls) {
      var ridx = rls.findIndex(function (i) {
        return i.ruleCode === ruleCode;
      });

      if (ridx > -1) {
        var nextRule = modifier(rls[ridx]);
        return modifyArrayItem(rls, ridx, function (r) {
          return nextRule ? _extends({}, nextRule, {
            disabled: false
          }) : _extends({}, r, {
            disabled: true
          });
        });
      }

      return rls;
    });
  }, []);
  return compiledRules === null && !staticBuild ? null : /*#__PURE__*/React.createElement(PricingRulesContext.Provider, {
    value: {
      rules: compiledRules,
      currency: currency,
      decimalPrecision: decimalPrecision,
      noTaxPrices: noTaxPrices,
      dynamicRuleEnable: dynamicRuleEnable,
      dynamicRuleDisable: dynamicRuleDisable,
      priceGroup: priceGroup,
      region: region,
      productFinderHook: productFinderHook,
      strategies: strategies
    }
  }, children);
}

process.env.NODE_ENV !== "production" ? PricingRulesProvider.propTypes = {
  children: PropTypes.node,
  currency: PropTypes.string.isRequired,
  decimalPrecision: PropTypes.number,
  initialRules: PropTypes.arrayOf(PropTypes.shape({
    disabled: PropTypes.bool,
    map: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
    noFootprint: PropTypes.bool,
    ruleCode: PropTypes.string.isRequired,
    ruleName: PropTypes.string.isRequired,
    ruleThread: PropTypes.string,
    ruleType: PropTypes.oneOf(['subject', 'cart']).isRequired,
    superGroup: PropTypes.number.isRequired,
    targets: PropTypes.arrayOf(PropTypes.shape({
      constraints: PropTypes.array,
      triggers: PropTypes.array
    })),
    validFrom: PropTypes.instanceOf(Date),
    validTo: PropTypes.instanceOf(Date),
    valueAdded: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
  })),
  initialRulesCompiled: PropTypes.arrayOf(PropTypes.any),
  noTaxPrices: PropTypes.bool.isRequired,
  priceGroup: PropTypes.string,
  productFinderHook: PropTypes.func,
  region: PropTypes.string,
  rulesValidTreshold: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  staticBuild: PropTypes.bool,
  strategies: PropTypes.shape({
    constraint: PropTypes.object,
    forceThreadFootprint: PropTypes.object,
    innerCondition: PropTypes.object,
    map: PropTypes.object,
    rule: PropTypes.object,
    trigger: PropTypes.object,
    valueAdded: PropTypes.object
  })
} : void 0;
PricingRulesProvider.defaultProps = {
  children: null,
  decimalPrecision: undefined,
  initialRules: undefined,
  initialRulesCompiled: undefined,
  priceGroup: undefined,
  productFinderHook: undefined,
  region: undefined,
  rulesValidTreshold: undefined,
  staticBuild: undefined,
  strategies: {}
};
export default PricingRulesProvider;